<template>
  <div
    id="app"
    :class="{
      'ai-assistant-open': showAiAssistant,
      'ai-assistant-fullscreen': showAiAssistant && chatSize === 'full',
    }"
  >
    <simulate-user-modal v-model="simulateUserModalVisible" />

    <!-- Mobile hamburger (only on mobile) -->
    <div v-if="isAuthenticated" class="gid-mobile-header d-lg-none">
      <b-button
        aria-label="Toggle navigation"
        @click="sidebarToggle"
        variant="primary"
        class="gid-navigation-toggler"
      >
        <BIconList />
      </b-button>
    </div>

    <!-- Sidebar outside of gid-layout for proper fixed positioning -->
    <div
      v-if="isAuthenticated"
      :class="[
        'border-right',
        'gid-sidebar',
        isMobile ? 'gid-sidebar-mobile' : 'gid-sidebar-desktop',
        { 'gid-sidebar-mobile-closed': isMobile && !sidebarOpen },
        { 'gid-sidebar-mobile-open': isMobile && sidebarOpen },
        { 'gid-sidebar-desktop-collapsed': !isMobile && !sidebarExpanded },
        { 'gid-sidebar-desktop-expanded': !isMobile && sidebarExpanded },
      ]"
      @mouseenter="handleSidebarMouseEnter"
      @mouseleave="handleSidebarMouseLeave"
    >
      <div class="gid-sidebar-content">
        <!-- Branding at top -->
        <div class="gid-sidebar-header">
          <router-link
            to="/"
            class="sidebar-brand-link"
            @click.native="sidebarClose"
          >
            <div class="d-flex align-items-center px-3 py-3">
              <env-orbit-logo
                :environment="environment"
                :radius="12"
                :duration="12"
                font-size="0.55rem"
                class="mr-2"
              >
                <img
                  src="/icons/s4e-mark.svg"
                  alt="Service4Energy"
                  class="sidebar-icon sidebar-brand-logo"
                />
              </env-orbit-logo>
              <div
                class="sidebar-brand-text"
                :class="{
                  'sidebar-text-visible': sidebarExpanded || isMobile,
                }"
              >
                <span class="sidebar-brand-name">
                  Service
                  <span class="sidebar-brand-accent">4Energy</span>
                </span>
                <span class="sidebar-brand-sub">ADMIN</span>
              </div>
            </div>
          </router-link>
        </div>

        <!-- Close button for mobile -->
        <button
          class="gid-sidebar-close-btn d-lg-none"
          @click="sidebarClose"
          aria-label="Close menu"
        >
          <span>&times;</span>
        </button>

        <b-navbar-nav class="py-2">
          <template v-for="(item, index) in navItemsFiltered">
            <!-- Main menu items without children -->
            <b-nav-item
              v-if="!item.children"
              :key="index"
              :to="item.route"
              link-classes="px-3 d-flex align-items-center justify-content-start"
              @click="sidebarClose"
              v-b-tooltip.hover.noninteractive="{
                title: !(sidebarExpanded || isMobile) ? item.label : '',
                placement: 'right',
                boundary: 'viewport',
                offset: 10,
              }"
            >
              <component :is="item.icon" class="sidebar-icon" />
              <span
                class="ml-2 sidebar-text"
                :class="{
                  'sidebar-text-visible': sidebarExpanded || isMobile,
                }"
              >
                {{ item.label }}
              </span>
              <ProductOrdersPendingCount
                v-if="
                  item.label === 'Product orders' &&
                  (sidebarExpanded || isMobile)
                "
                class="ml-auto"
              />
            </b-nav-item>

            <!-- Main menu items with children -->
            <div
              v-else
              :key="index"
              :class="{ 'border-bottom': isMenuItemExpanded(item) }"
              @mouseenter="handleItemMouseEnter(item)"
              @mouseleave="handleItemMouseLeave(item)"
            >
              <b-nav-item
                link-classes="px-3 d-flex align-items-center justify-content-start"
                @click.prevent="toggleSection(item)"
                href="#"
                v-b-tooltip.hover.noninteractive="{
                  title: !(sidebarExpanded || isMobile) ? item.label : '',
                  placement: 'right',
                  boundary: 'viewport',
                  offset: 10,
                }"
              >
                <component :is="item.icon" class="sidebar-icon" />
                <span
                  class="ml-2 sidebar-text"
                  :class="{
                    'sidebar-text-visible': sidebarExpanded || isMobile,
                  }"
                >
                  {{ item.label }}
                </span>
                <BIconChevronDown
                  v-if="sidebarExpanded || isMobile"
                  class="ml-auto"
                  :class="{ 'rotate-180': isMenuItemExpanded(item) }"
                />
              </b-nav-item>

              <!-- Sub-items -->
              <transition name="expand">
                <div v-if="isMenuItemExpanded(item)" class="submenu">
                  <b-nav-item
                    v-for="(child, childIndex) in item.children"
                    :key="childIndex"
                    :to="child.route"
                    :href="child.href"
                    :target="child.target"
                    link-classes="px-3 pl-4 d-flex align-items-center submenu-item justify-content-start"
                    @click="handleSubItemClick(child, $event)"
                    v-b-tooltip.hover.noninteractive="{
                      title: !(sidebarExpanded || isMobile) ? child.label : '',
                      placement: 'right',
                      boundary: 'viewport',
                      offset: 10,
                    }"
                  >
                    <component
                      v-if="child.icon"
                      :is="child.icon"
                      class="sidebar-icon"
                      style="font-size: 1rem"
                    />
                    <BIconCircleFill
                      v-else
                      class="sidebar-icon"
                      style="font-size: 0.5rem"
                    />
                    <span
                      class="ml-2 sidebar-text"
                      :class="{
                        'sidebar-text-visible': sidebarExpanded || isMobile,
                      }"
                    >
                      {{ child.label }}
                    </span>
                  </b-nav-item>
                </div>
              </transition>
            </div>
          </template>

          <!-- AI Assistant Toggle -->
          <b-nav-item
            v-if="hasAiAssistantAccess"
            @click="toggleAiAssistant"
            href="#"
            link-classes="px-3 d-flex align-items-center ai-assistant-toggle justify-content-start"
            v-b-tooltip.hover.noninteractive="{
              title: !(sidebarExpanded || isMobile)
                ? 'AI Assistant (Ctrl+/)'
                : '',
              placement: 'right',
              boundary: 'viewport',
              offset: 10,
            }"
          >
            <BIconChatDots class="sidebar-icon" />
            <span
              class="ml-2 sidebar-text"
              :class="{ 'sidebar-text-visible': sidebarExpanded || isMobile }"
            >
              AI Assistant
            </span>
            <small v-if="sidebarExpanded || isMobile" class="ml-1 text-muted">
              (Ctrl+/)
            </small>
          </b-nav-item>
        </b-navbar-nav>
      </div>

      <!-- Lock/Logout at bottom -->
      <div class="gid-sidebar-bottom">
        <b-navbar-nav>
          <!-- Theme toggle: switch between light and dark -->
          <b-nav-item
            @click="toggleTheme"
            href="#"
            link-classes="px-3 d-flex align-items-center justify-content-start"
            v-b-tooltip.hover.noninteractive="{
              title: !(sidebarExpanded || isMobile) ? themeTooltip : '',
              placement: 'right',
              boundary: 'viewport',
              offset: 10,
            }"
          >
            <BIconMoonStarsFill v-if="isDarkMode" class="sidebar-icon" />
            <BIconBrightnessHighFill v-else class="sidebar-icon" />
            <span
              class="ml-2 sidebar-text"
              :class="{ 'sidebar-text-visible': sidebarExpanded || isMobile }"
            >
              {{ themeLabel }}
            </span>
          </b-nav-item>
          <!-- Lock menu button (desktop only) -->
          <b-nav-item
            v-if="!isMobile"
            @click="toggleSidebarLock"
            href="#"
            link-classes="px-3 d-flex align-items-center justify-content-start"
            v-b-tooltip.hover.right.noninteractive="
              !sidebarExpanded
                ? sidebarLocked
                  ? 'Unlock menu (enable hover expand)'
                  : 'Lock menu (disable hover expand)'
                : ''
            "
          >
            <BIconLock v-if="sidebarLocked" class="sidebar-icon" />
            <BIconUnlock v-else class="sidebar-icon" />
            <span
              class="ml-2 sidebar-text"
              :class="{ 'sidebar-text-visible': sidebarExpanded }"
            >
              {{ sidebarLocked ? 'Unlock menu' : 'Lock menu' }}
            </span>
          </b-nav-item>
          <b-nav-item
            @click="logout"
            link-classes="px-3 d-flex align-items-center justify-content-start"
            v-b-tooltip.hover.noninteractive="{
              title: !(sidebarExpanded || isMobile) ? 'Logout' : '',
              placement: 'right',
              boundary: 'viewport',
              offset: 10,
            }"
          >
            <BIconBoxArrowRight class="sidebar-icon" />
            <span
              class="ml-2 sidebar-text"
              :class="{ 'sidebar-text-visible': sidebarExpanded || isMobile }"
            >
              Logout
            </span>
          </b-nav-item>
        </b-navbar-nav>
      </div>
    </div>

    <div v-if="isAuthenticated" class="gid-layout">
      <b-container fluid class="gid-main-content">
        <b-alert
          v-if="notFoundUrl"
          variant="warning"
          dismissible
          show
          class="mb-3"
          @dismissed="clearNotFound"
        >
          {{ $t('not-found-redirect', { url: notFoundUrl }) }}
        </b-alert>
        <back-button class="mr-2 mb-3" />
        <refresh-button
          v-if="updateExists"
          :onRefresh="refreshApp"
          class="mb-3"
        />
        <bootstrap-loader v-if="hasAccess === null" />
        <router-view v-else-if="hasAccess" />
        <div v-else class="text-center">
          <h4 class="m-5">{{ $t('no-access.title') }}</h4>
          <p v-if="isAuthenticated">{{ $t('no-access.description') }}</p>
          <i18n v-else path="no-access.login" tag="p" class="mb-0">
            <router-link slot="login" to="/login">
              {{ $t('no-access.login-link') }}
            </router-link>
          </i18n>
        </div>
      </b-container>
    </div>

    <!-- Not authenticated -->
    <div v-else>
      <b-container fluid>
        <b-spinner v-if="hasAccess === null" variant="secondary" class="m-2" />
        <router-view v-else-if="hasAccess" />
        <div v-else class="text-center">
          <h4 class="m-5">{{ $t('no-access.title') }}</h4>
          <i18n path="no-access.login" tag="p" class="mb-0">
            <router-link slot="login" to="/login">
              {{ $t('no-access.login-link') }}
            </router-link>
          </i18n>
        </div>
      </b-container>
    </div>

    <!-- Global AI Assistant -->
    <llama-chat
      v-if="hasAiAssistantAccess"
      v-show="showAiAssistant"
      position="right"
      :chat-backend="CHAT_BACKEND.OPENCLAW"
      @close="showAiAssistant = false"
      @size-change="chatSize = $event"
    />

    <add-to-home-screen app-name="GID Admin" />
    <offline />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { PURGE_AUTH } from '@gid/vue-common/store/auth.module/types';
import update from '@gid/vue-common/components/mixins/update';
import { appUrl } from '@gid/vue-common/app-settings/appUrls';
import { checkRouteAccess } from '@gid/vue-common/store/auth.module';
import { getEnv } from '@gid/vue-common/utils';
import ProductOrdersPendingCount from './views/product-orders/ProductOrdersPendingCount.vue';
import SimulateUserModal from './components/SimulateUserModal.vue';
import LlamaChat from '@gid/vue-common/components/chat-view/llama-chat.vue';
import { CHAT_BACKEND } from '@gid/vue-common/components/chat-view/constants';
import EnvOrbitLogo from '@gid/vue-common/components/EnvOrbitLogo.vue';
import BootstrapLoader from '@gid/vue-common/components/BootstrapLoader.vue';
import {
  ContactProfileEnum,
  isConfinedProfileSet,
} from '@gid/models/dist/entities/contact-profile-enum';
import { holdingLandingRoute } from '@/router';
import {
  BIcon,
  BIconBriefcase,
  BIconPeople,
  BIconPuzzle,
  BIconBoxSeam,
  BIconCalculator,
  BIconTable,
  BIconWrench,
  BIconChatDots,
  BIconBoxArrowRight,
  BIconChevronDown,
  BIconList,
  BIconGrid,
  BIconCircleFill,
  BIconStarFill,
  BIconClipboardData,
  BIconBoxArrowUpRight,
  BIconFileEarmarkText,
  BIconFiles,
  BIconCreditCard,
  BIconGraphUp,
  BIconBarChartLineFill,
  BIconFunnelFill,
  BIconArrowRepeat,
  BIconGear,
  BIconDownload,
  BIconEnvelope,
  BIconBook,
  BIconCode,
  BIconInboxFill,
  BIconListTask,
  BIconServer,
  BIconFileEarmarkArrowUp,
  BIconPersonCircle,
  BIconLock,
  BIconUnlock,
  BIconMoonStarsFill,
  BIconBrightnessHighFill,
  BIconBuilding,
  BIconLifePreserver,
  BIconWhatsapp,
  BIconGeoAlt,
} from 'bootstrap-vue';

const NAV_ITEMS = [
  {
    label: 'Jobs',
    route: '/jobs',
    routeName: 'jobs',
    icon: BIconBriefcase,
  },
  {
    label: 'Tickets',
    route: '/tickets',
    routeName: 'tickets',
    icon: BIconLifePreserver,
  },
  {
    // Hidden for anyone without the GID contact profile: navItemsFiltered ->
    // canAccessRoute -> the route's own meta.auth gate.
    label: 'WhatsApp',
    route: '/whatsapp',
    routeName: 'whatsapp',
    icon: BIconWhatsapp,
  },
  {
    label: 'Partners',
    icon: BIconPeople,
    expanded: false,
    routeName: 'partners',
    children: [
      {
        label: 'Partner List',
        route: '/partners',
        routeName: 'partners',
        icon: BIconPeople,
      },
      {
        label: 'Partner Map',
        route: '/partners/map',
        routeName: 'partners-map',
        icon: BIconGeoAlt,
      },
      {
        label: 'S4C Auslastung',
        route: '/s4c/utilization',
        routeName: 's4c-utilization',
        icon: BIconBarChartLineFill,
      },
    ],
  },
  {
    label: 'Brands',
    route: '/brands',
    routeName: 'brands',
    icon: BIconBuilding,
  },
  {
    label: 'Integrations',
    icon: BIconPuzzle,
    expanded: false,
    children: [
      {
        label: 'Rating Admin',
        route: '/rating-admin',
        routeName: 'rating-admin',
        icon: BIconStarFill,
      },
      {
        label: 'Order Intake Forms Admin',
        route: '/order-intake-forms-admin',
        routeName: 'order-intake-forms-admin',
        icon: BIconClipboardData,
      },
      {
        label: 'OI Forms Admin in New Tab',
        routeName: 'order-intake-forms-admin',
        external: true,
        icon: BIconBoxArrowUpRight,
      },
      {
        label: 'Entwurf Dynamics-Migration',
        route: '/dynamics-migration-plan',
        routeName: 'dynamics-migration-plan',
        icon: BIconFileEarmarkText,
      },
    ],
  },
  {
    label: 'Product orders',
    route: '/product-orders',
    routeName: 'product-orders',
    icon: BIconBoxSeam,
  },
  {
    label: 'Accounting',
    icon: BIconCalculator,
    expanded: false,
    routeName: 'accounting',
    children: [
      {
        label: 'Draft Documents',
        route: '/accounting/drafts',
        routeName: 'accounting-drafts',
        icon: BIconFileEarmarkText,
      },
      {
        label: 'Documents',
        route: '/accounting/documents',
        routeName: 'accounting-documents',
        icon: BIconFiles,
      },
      {
        label: 'Payments',
        route: '/accounting/payments',
        routeName: 'accounting-payments',
        icon: BIconCreditCard,
      },
      {
        label: 'Documents Balances',
        route: '/accounting/documents-balances',
        routeName: 'documents-balances',
        icon: BIconGraphUp,
      },
      {
        label: 'Accounts Aging',
        route: '/accounting/aging',
        routeName: 'accounting-aging',
        icon: BIconBarChartLineFill,
      },
      {
        label: 'S4C Aging (BC)',
        route: '/accounting/s4c-aging',
        routeName: 'accounting-s4c-aging',
        icon: BIconBarChartLineFill,
      },
      {
        label: 'Revenue Funnel',
        route: '/accounting/revenue-funnel',
        routeName: 'accounting-revenue-funnel',
        icon: BIconFunnelFill,
      },
      {
        label: 'Recheck Drafts',
        route: '/accounting/recheck-drafts',
        routeName: 'accounting-recheck-drafts',
        icon: BIconArrowRepeat,
      },
      {
        label: 'Configuration',
        route: '/accounting/configuration',
        routeName: 'accounting-configuration',
        icon: BIconGear,
      },
      {
        label: 'Exports',
        route: '/accounting/exports',
        routeName: 'accounting-exports',
        icon: BIconDownload,
      },
    ],
  },
  {
    label: 'Data',
    route: '/data',
    routeName: 'data',
    icon: BIconTable,
  },
  {
    label: 'Tools',
    icon: BIconWrench,
    expanded: false,
    routeName: 'tools',
    children: [
      {
        label: 'Email Tester',
        route: '/email-tester',
        routeName: 'email-tester',
        icon: BIconEnvelope,
      },

      {
        label: 'Email Queues',
        route: '/email-queues',
        routeName: 'email-queues',
        icon: BIconInboxFill,
      },
      {
        label: 'Jobs Service Queues',
        route: '/jobs-service-queues',
        routeName: 'jobs-service-queues',
        icon: BIconListTask,
      },
      {
        label: 'Crud Queues',
        route: '/crud-queues',
        routeName: 'crud-queues',
        icon: BIconServer,
      },
      {
        label: 'Documents Service Queues',
        route: '/documents-service-queues',
        routeName: 'documents-service-queues',
        icon: BIconFileEarmarkArrowUp,
      },
      {
        label: 'Simulate User',
        action: 'simulateUser',
        requiresTechnicalSupport: true,
        icon: BIconPersonCircle,
      },
    ],
  },
];

export default {
  components: {
    ProductOrdersPendingCount,
    SimulateUserModal,
    LlamaChat,
    EnvOrbitLogo,
    BootstrapLoader,
    BIcon,
    BIconBriefcase,
    BIconWhatsapp,
    BIconPeople,
    BIconPuzzle,
    BIconBoxSeam,
    BIconCalculator,
    BIconTable,
    BIconWrench,
    BIconChatDots,
    BIconBoxArrowRight,
    BIconChevronDown,
    BIconList,
    BIconGrid,
    BIconCircleFill,
    BIconStarFill,
    BIconClipboardData,
    BIconBoxArrowUpRight,
    BIconFileEarmarkText,
    BIconFiles,
    BIconCreditCard,
    BIconGraphUp,
    BIconBarChartLineFill,
    BIconArrowRepeat,
    BIconGear,
    BIconDownload,
    BIconEnvelope,
    BIconBook,
    BIconCode,
    BIconInboxFill,
    BIconListTask,
    BIconServer,
    BIconFileEarmarkArrowUp,
    BIconPersonCircle,
    BIconLock,
    BIconUnlock,
    BIconMoonStarsFill,
    BIconBrightnessHighFill,
  },
  mixins: [update],
  computed: {
    ...mapGetters([
      'access_token',
      'isAuthenticated',
      'environment',
      'contactProfiles',
      'user',
    ]),
    isTechnicalSupport() {
      return this.contactProfiles.includes(
        ContactProfileEnum.TECHNICAL_SUPPORT,
      );
    },
    hasAccess() {
      return checkRouteAccess({
        route: this.$route,
        user: this.user,
        isAuthenticated: this.isAuthenticated,
      });
    },
    formsBackofficeUrl() {
      const _url = `${appUrl(
        'order-intake-forms-backoffice',
        '/',
      )}?${new URLSearchParams({ jwt: this.access_token })}`;
      console.log('constr url', _url);
      return _url;
    },
    notFoundUrl() {
      return this.$route.query.notFound || null;
    },
    hasAiAssistantAccess() {
      const allowedProfiles = [
        ContactProfileEnum.CUSTOMER_MANAGEMENT,
        ContactProfileEnum.PARTNER_ONBOARDING,
        ContactProfileEnum.BRAND_MANAGEMENT,
        ContactProfileEnum.GID,
        ContactProfileEnum.PARTNER_MANAGEMENT,
        ContactProfileEnum.FINANCE,
        ContactProfileEnum.FINANCE_POWER_USER,
        ContactProfileEnum.PRODUCT,
        ContactProfileEnum.PRODUCT_POWER_USER,
        ContactProfileEnum.TECHNICAL_SUPPORT,
      ];
      return (
        this.isAuthenticated &&
        allowedProfiles.some((profile) =>
          this.contactProfiles.includes(profile),
        )
      );
    },
    navItemsFiltered() {
      // Clone items but preserve icon component references
      const items = NAV_ITEMS.map((item) => {
        const clonedItem = { ...item };
        if (item.children) {
          clonedItem.children = item.children.map((child) => ({ ...child }));
        }
        return clonedItem;
      });

      return items.filter((item) => {
        if (item.children) {
          item.children = item.children.filter((child) => {
            if (child.requiresTechnicalSupport && !this.isTechnicalSupport) {
              return false;
            }
            if (child.external) {
              child.href = this.formsBackofficeUrl;
              child.target = '_blank';
              return this.canAccessRoute({ name: child.routeName });
            }
            return child.routeName
              ? this.canAccessRoute({ name: child.routeName })
              : true;
          });
          if (item.children.length === 0) {
            return false;
          }
          // Initialize expanded property reactively
          if (!Object.prototype.hasOwnProperty.call(item, 'expanded')) {
            this.$set(item, 'expanded', false);
          }
          const hasAccess = item.routeName
            ? this.canAccessRoute({ name: item.routeName })
            : item.children.some((child) =>
                child.routeName
                  ? this.canAccessRoute({ name: child.routeName })
                  : false,
              );
          return hasAccess;
        }
        return item.routeName
          ? this.canAccessRoute({ name: item.routeName })
          : true;
      });
    },
    isMobile() {
      return this.windowWidth < 992; // Bootstrap lg breakpoint
    },
    isDarkMode() {
      return this.$accessibility?.isDarkMode ?? false;
    },
    themeLabel() {
      return this.isDarkMode ? 'Dark mode' : 'Light mode';
    },
    themeTooltip() {
      return this.isDarkMode ? 'Switch to light mode' : 'Switch to dark mode';
    },
  },
  data() {
    return {
      CHAT_BACKEND,
      simulateUserModalVisible: false,
      showAiAssistant: false,
      chatSize: 'default',
      sidebarOpen: false,
      sidebarExpanded: false,
      sidebarLocked: localStorage.getItem('sidebarLocked') === 'true',
      hoverTimeout: null,
      expandedMenuItems: {},
      windowWidth: window.innerWidth,
    };
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyboardShortcuts);
    window.addEventListener('resize', this.handleWindowResize);
    // The high-contrast display variant has been retired — the sidebar eye now
    // toggles light/dark only. Clear any state persisted by the old control.
    this.$accessibility?.setHighContrast(false);
  },
  watch: {
    sidebarLocked(newValue) {
      localStorage.setItem('sidebarLocked', newValue ? 'true' : 'false');
    },
    // The router guard in main.js catches in-app navigation to Home, but on a
    // hard load of "/" the user record has not arrived yet, so the profiles read
    // as empty and the guard lets it through. This fires once they land.
    user: {
      immediate: true,
      handler() {
        this.redirectConfinedAwayFromHome();
      },
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyboardShortcuts);
    window.removeEventListener('resize', this.handleWindowResize);
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
  },
  methods: {
    redirectConfinedAwayFromHome() {
      if (
        this.$route.name === 'home' &&
        isConfinedProfileSet(this.contactProfiles)
      ) {
        this.$router.replace(holdingLandingRoute()).catch(() => {});
      }
    },
    clearNotFound() {
      if (this.$route.query.notFound) {
        const query = { ...this.$route.query };
        delete query.notFound;
        this.$router.replace({ query });
      }
    },
    handleWindowResize() {
      this.windowWidth = window.innerWidth;
      // Close mobile sidebar if transitioning to desktop
      if (!this.isMobile && this.sidebarOpen) {
        this.sidebarOpen = false;
      }
    },
    logout() {
      this.$store.commit(PURGE_AUTH);
    },
    toggleTheme() {
      this.$accessibility?.toggleDarkMode();
    },
    canAccessRoute(routes) {
      if (!Array.isArray(routes)) {
        routes = [routes];
      }
      for (const route of routes) {
        const resolved = this.$router.resolve(route);
        if (
          resolved &&
          checkRouteAccess({
            route: resolved.route,
            user: this.user,
            isAuthenticated: this.isAuthenticated,
          })
        ) {
          return true;
        }
      }
      return false;
    },
    toggleAiAssistant() {
      this.showAiAssistant = !this.showAiAssistant;
    },
    toggleSidebarLock() {
      this.sidebarLocked = !this.sidebarLocked;
      // When locking, ensure sidebar is collapsed
      if (this.sidebarLocked) {
        this.sidebarExpanded = false;
      }
    },
    handleSidebarMouseEnter() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
      // Don't expand on hover if sidebar is locked
      if (!this.isMobile && !this.sidebarLocked) {
        this.hoverTimeout = setTimeout(() => {
          this.sidebarExpanded = true;
        }, 50);
      }
    },
    handleSidebarMouseLeave() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
      // Don't collapse on leave if sidebar is locked (already collapsed)
      if (!this.isMobile && !this.sidebarLocked) {
        this.hoverTimeout = setTimeout(() => {
          this.sidebarExpanded = false;
          // Keep expandedMenuItems state persistent
        }, 100);
      }
    },
    sidebarToggle() {
      this.sidebarOpen = !this.sidebarOpen;
      // On mobile, always expand the sidebar when opened
      if (this.sidebarOpen && this.isMobile) {
        this.sidebarExpanded = true;
      }
    },
    sidebarClose() {
      this.sidebarOpen = false;
      // Reset expanded state on mobile when closing
      if (this.isMobile) {
        this.sidebarExpanded = false;
      }
    },
    isMenuItemExpanded(item) {
      return !!this.expandedMenuItems[item.label];
    },
    setMenuItemExpanded(item, expanded) {
      this.$set(this.expandedMenuItems, item.label, expanded);
    },
    toggleSection(item) {
      // Allow toggling in both collapsed and expanded states
      this.setMenuItemExpanded(item, !this.isMenuItemExpanded(item));
    },
    handleItemMouseEnter(item) {
      // Sub-items expand on click only, not on hover
    },
    handleItemMouseLeave(item) {
      // Sub-items expand on click only, not on hover
    },
    handleSubItemClick(child, event) {
      if (child.action === 'simulateUser') {
        event.preventDefault();
        this.simulateUserModalVisible = true;
        this.sidebarClose();
      } else if (!child.external) {
        this.sidebarClose();
      }
    },
    handleKeyboardShortcuts(event) {
      if (!this.hasAiAssistantAccess) {
        return;
      }

      const isShortcutKey = event.key === '/' || event.key === 'ß';
      const isCtrlShortcut = (event.ctrlKey || event.metaKey) && isShortcutKey;

      if (!isShortcutKey) {
        return;
      }

      const activeElement = document.activeElement;
      const isInInputField =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.isContentEditable ||
          activeElement.closest('[contenteditable="true"]'));

      // Ctrl+/ or Ctrl+ß works everywhere, even in input fields
      if (isCtrlShortcut) {
        const isInAssistant =
          activeElement && activeElement.closest('.llama-chat-container');
        const isInAssistantTextarea =
          isInAssistant && activeElement.tagName === 'TEXTAREA';

        // Toggle assistant unless typing in the assistant's textarea
        if (!isInAssistantTextarea) {
          event.preventDefault();
          this.showAiAssistant = !this.showAiAssistant;
        }
        return;
      }

      // Regular / or ß (without Ctrl) only works when NOT in input fields
      if (!this.showAiAssistant && !isInInputField) {
        event.preventDefault();
        this.showAiAssistant = true;
        return;
      }

      if (this.showAiAssistant) {
        const isInAssistant =
          activeElement && activeElement.closest('.llama-chat-container');
        const isInAssistantTextarea =
          isInAssistant && activeElement.tagName === 'TEXTAREA';

        if (!isInAssistantTextarea) {
          event.preventDefault();
          this.showAiAssistant = false;
        }
      }
    },
  },
};
</script>
<style lang="scss">
@import '@gid/vue-common/scss/_variables.scss';
@import './assets/scss/brand';
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins/breakpoints';

:root {
  --sidebar-width-collapsed: 59px;
  --sidebar-width-expanded: 240px;
  --mobile-header-height: 56px;
  // Admin desktop has no top header — the sidebar carries the brand. The chat
  // panel uses --header-height for its top inset (falls back to 62px otherwise),
  // so set 0 here to let it span the full viewport height. Mobile still has
  // .gid-mobile-header (z-1032) covering the top strip, so the chat reclaims
  // the mobile-header-height there to avoid hiding the action bar behind it.
  --header-height: 0px;
}

@include media-breakpoint-down(md) {
  :root {
    --header-height: var(--mobile-header-height);
  }
}

// Ensure proper viewport handling for fixed elements
html,
body {
  height: 100%;
}

body {
  overflow-y: auto;
}

#app {
  min-height: 100%;
  position: relative;
}

.gid-mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  height: var(--mobile-header-height);
  background: $s4e-ink;
  color: white;
  display: flex;
  align-items: center;
  padding: 0.5rem;
  z-index: 1032;

  .gid-navigation-toggler {
    padding: 0.25rem 0.5rem;
    font-size: 1.5rem;
  }

  @include media-breakpoint-up(lg) {
    display: none !important;
  }
}

.gid-layout {
  display: flex;
  min-height: 100vh;

  @include media-breakpoint-down(md) {
    padding-top: var(--mobile-header-height);
  }
}

// Hide the main content area when the AI assistant is in fullscreen mode.
// The sidebar (.gid-sidebar) stays mounted because it lives outside .gid-layout,
// so the user keeps access to navigation while the chat fills the rest of the
// viewport. The class on #app is set reactively from the chat's @size-change
// event, so closing the chat (any path that flips showAiAssistant) automatically
// removes it.
#app.ai-assistant-fullscreen .gid-layout {
  display: none !important;
}

.gid-main-content {
  flex: 1;
  padding: 1.5rem;
}

.container-fluid {
  padding-top: 0px;

  @include media-breakpoint-up(lg) {
    margin-left: 59px !important;
    max-width: calc(100% - 59px) !important;
  }
}

.fixed-bottom.actions-menu,
.fixed-bottom.gid-a2hs__alert {
  @include media-breakpoint-up(lg) {
    left: var(--sidebar-width-collapsed);
    width: calc(100% - var(--sidebar-width-collapsed));
  }
}

.gid-sidebar {
  position: fixed;
  z-index: 1031;
  left: 0;
  top: 0;
  bottom: 0;
  height: 100vh;
  height: 100dvh; // Dynamic viewport height for mobile browsers
  max-height: 100vh;
  max-height: 100dvh;
  background: $white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  // Force GPU layer to fix Chrome DevTools device toolbar issues
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;

  a:not(.router-link-active) {
    color: inherit;
    &:hover {
      color: $primary;
    }
  }

  // Mobile-specific styles
  &-mobile {
    top: var(--mobile-header-height);
    bottom: 0;
    height: calc(100vh - var(--mobile-header-height));
    height: calc(100dvh - var(--mobile-header-height));
    max-height: calc(100vh - var(--mobile-header-height));
    max-height: calc(100dvh - var(--mobile-header-height));
    width: 100%;
    transform: translateX(-100%);
    transition: transform 0.3s ease;

    &-closed {
      transform: translateX(-100%);
    }

    &-open {
      transform: translateX(0);
    }
  }

  // Desktop-specific styles
  &-desktop {
    width: var(--sidebar-width-collapsed);
    transition: width 0.2s ease;
    will-change: width;

    &-collapsed {
      width: var(--sidebar-width-collapsed);
    }

    &-expanded {
      width: var(--sidebar-width-expanded);
    }
  }

  &-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &-header {
    flex-shrink: 0;
    border-bottom: 1px solid $dyn-border;
    background: $dyn-canvas;

    .sidebar-brand-link {
      text-decoration: none;
      color: inherit;
      display: block;

      &:hover {
        background: $dyn-subtle;
      }
    }

    .sidebar-brand-text {
      display: flex;
      flex-direction: column;
      line-height: 1.15;
      white-space: nowrap;
      opacity: 0;
      overflow: hidden;
      transition: opacity 0.15s ease;

      &.sidebar-text-visible {
        opacity: 1;
      }
    }

    .sidebar-brand-name {
      font-family: $s4e-font-brand;
      font-weight: 600;
      font-size: 1.05rem;
      letter-spacing: -0.01em;
      color: $s4e-ink;
    }

    .sidebar-brand-accent {
      color: $s4e-gold;
    }

    .sidebar-brand-sub {
      font-size: 0.62rem;
      font-weight: 600;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: $dyn-text-muted;
    }
  }

  &-close-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: transparent;
    border: none;
    font-size: 2rem;
    line-height: 1;
    color: $gray-600;
    cursor: pointer;
    z-index: 10;
    padding: 0;
    width: 2rem;
    height: 2rem;

    &:hover {
      color: $gray-900;
    }
  }

  .nav-link {
    position: relative;
    white-space: nowrap;
    transition: all 0.15s ease;
    padding-top: 0.75rem !important;
    padding-bottom: 0.75rem !important;
    display: flex !important;
    align-items: center !important;

    &.router-link-active {
      background: $dyn-subtle;
      color: $s4e-ink;
      font-weight: 600;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 6px;
        bottom: 6px;
        width: 3px;
        border-radius: 0 2px 2px 0;
        background: $s4e-gold;
      }

      .sidebar-icon {
        color: $s4e-gold;
      }
    }

    &:hover {
      background: $dyn-subtle;
    }
  }

  .sidebar-icon {
    font-size: 1.25rem;
    flex-shrink: 0;
    transition: transform 0.15s ease;
    width: 1.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .sidebar-icon img {
    height: 1.25rem;
    object-fit: contain;
  }

  // Brand logo (top-left) — 2× the standard nav-icon size
  .gid-sidebar-header .sidebar-brand-logo {
    width: 2.5rem;
    height: 2.5rem;
    object-fit: contain;
  }

  // In the collapsed/locked rail, center the logo within the rail width
  // (the wordmark is pulled from flow so the mark sits dead-center).
  &.gid-sidebar-desktop-collapsed {
    .gid-sidebar-header .d-flex {
      justify-content: center;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .gid-sidebar-header .env-orbit-wrapper {
      margin-right: 0 !important;
    }

    .gid-sidebar-header .sidebar-brand-text {
      display: none;
    }
  }

  .sidebar-text {
    transition: opacity 0.15s ease;
    overflow: hidden;
    will-change: opacity;
    opacity: 0;
    white-space: nowrap;

    &.sidebar-text-visible {
      opacity: 1;
    }
  }

  .submenu {
    background: $gray-100;

    .submenu-item {
      font-size: 0.9rem;
      padding-top: 0.5rem !important;
      padding-bottom: 0.5rem !important;
    }
  }

  .border-bottom {
    border-bottom: 1px solid $gray-300 !important;
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .rotate-180 {
    transform: rotate(180deg);
    transition: transform 0.15s ease;
  }

  .gid-sidebar-bottom {
    flex-shrink: 0;
    border-top: 1px solid $gray-300;
    padding-top: 0.5rem;
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: opacity 0.15s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
}

.expand-enter-to,
.expand-leave {
  opacity: 1;
}

.ai-assistant-toggle {
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

@media (min-width: 769px) {
  .ai-assistant-open .gid-main-content {
    margin-right: min(38.2vw, 600px);
    width: calc(
      100vw - min(38.2vw, 600px) - var(--sidebar-width-collapsed) - 30px
    );
    max-width: calc(
      100vw - min(38.2vw, 600px) - var(--sidebar-width-collapsed) - 30px
    );
  }
}

@media (min-width: 1571px) {
  .ai-assistant-open .gid-main-content {
    margin-right: 600px;
    width: calc(100vw - 600px - var(--sidebar-width-collapsed) - 30px);
    max-width: calc(100vw - 600px - var(--sidebar-width-collapsed) - 30px);
  }
}

// Above 2200px the non-maximized chat is no longer capped at 600px — it grows
// with the viewport at 38.2vw (see vue-common llama-chat.vue). Shift the main
// content by the same amount so the jobs table and every view stay fully
// visible to the left of the chat instead of flowing behind it.
@media (min-width: 2200px) {
  .ai-assistant-open .gid-main-content {
    margin-right: 38.2vw;
    width: calc(100vw - 38.2vw - var(--sidebar-width-collapsed) - 30px);
    max-width: calc(100vw - 38.2vw - var(--sidebar-width-collapsed) - 30px);
  }
}

@media (max-width: 768px) {
  .ai-assistant-open .gid-main-content {
    display: none;
  }
}
</style>
